import { useSelector } from '@/api/state';
import { getJob } from '@/utils/stateAccessors';
import BasicField from '@payaca/components/basicField/BasicField';
import Button from '@payaca/components/button/Button';
import { ButtonStyleVariant } from '@payaca/components/button/enums';
import DropdownField from '@payaca/components/dropdownField/DropdownField';
import { InputStyleVariant } from '@payaca/components/inputWrapper/InputWrapper';
import QuickActionsElement from '@payaca/components/quickActionsTableCell/QuickActionsElement';
import ValidatedForm from '@payaca/components/validatedForm/ValidatedForm';
import { isInvoice } from '@payaca/helpers/jobStatusHelper';
import { JobPermissions } from '@payaca/permissions/jobs/job.permissions';
import { FieldValidationResult } from '@payaca/types/fieldValidationTypes';
import { JobStatus } from '@payaca/types/jobTypes';
import { Job } from '@payaca/types/jobTypesV2';
import { FC, useCallback, useMemo, useState, type JSX } from 'react';
import { PermissionGuard } from '../permissionGuard/PermissionGuard';
import './JobReferenceControl.sass';

type Props = {
  jobId: number;
  updateJob: (
    value: { [key: string]: any },
    onUpdateSuccess?: () => void
  ) => void;
};
const JobReferenceControl: FC<Props> = ({
  jobId,
  updateJob,
}: Props): JSX.Element => {
  const [isEditingJobReference, setIsEditingJobReference] = useState(false);

  const job: Job | undefined = useSelector((state) => getJob(state, jobId));

  const initialFormState = useMemo(() => {
    return {
      job: {
        customReference: job?.customReference,
        status: job?.status,
      },
    };
  }, [job]);

  const jobIsInvoice = useMemo(
    () => !!job && isInvoice(job.status),
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'job'
    [job?.status]
  );

  const renderFormContents = useCallback(
    (
      isValid: boolean,
      formState: {
        [key: string]: any;
      },
      validationState: {
        [key: string]: FieldValidationResult;
      },
      touchedState: {
        [key: string]: boolean;
      },
      onFieldChange: (value: { [key: string]: any }) => void,
      onFieldTouch: (fieldName: string) => void
    ) => {
      return (
        <>
          {jobIsInvoice ? (
            <h1>Invoice</h1>
          ) : (
            <DropdownField
              name={'job.status'}
              styleVariant={InputStyleVariant.OUTSIZE}
              value={formState.job.status}
              onChange={(value) => {
                const newStatus = value['job.status'];
                onFieldChange({ ['job.status']: newStatus });
                updateJob(formState);
              }}
              onTouch={onFieldTouch}
              options={[
                { label: 'Quote', value: JobStatus.NEW_QUOTE },
                { label: 'Estimate', value: JobStatus.NEW_ESTIMATE },
              ]}
            />
          )}

          {isEditingJobReference ? (
            <div className="edit-deal-reference-wrapper flex-container">
              <BasicField
                name="job.customReference"
                styleVariant={InputStyleVariant.OUTSIZE}
                value={formState.job.customReference || ''}
                onChange={(fieldChangeValue: any) => {
                  let value = fieldChangeValue['job.customReference'];
                  value = value.substring(0, 20);
                  onFieldChange({ ['job.customReference']: value });
                }}
                onTouch={onFieldTouch}
                additionalInputProps={{
                  placeholder: formState.reference,
                }}
              />
              <Button
                styleVariant={ButtonStyleVariant.ANCHOR}
                onClick={() => {
                  updateJob(formState);
                  setIsEditingJobReference(false);
                }}
              >
                Save
              </Button>
            </div>
          ) : (
            <h1># {job?.customReference || job?.reference}</h1>
          )}
        </>
      );
    },
    [isEditingJobReference, job, jobIsInvoice, updateJob]
  );

  return (
    <div className="job-reference-control" data-testid="job-reference-control">
      <ValidatedForm
        initialFormState={initialFormState}
        renderFormContents={renderFormContents}
      />
      <PermissionGuard
        renderIfHasPermissions={[JobPermissions.UPDATE_JOB_REFERENCE]}
      >
        {!job?.archivedAt && !isEditingJobReference && (
          <QuickActionsElement
            recordId={jobId}
            quickActions={[
              {
                actionName: 'Edit reference',
                actionBehaviour: () => {
                  setIsEditingJobReference(true);
                },
              },
            ]}
          />
        )}
      </PermissionGuard>
    </div>
  );
};
export default JobReferenceControl;
